import { IconArrowRight } from "@tabler/icons-react";
import { format, max, min, parseISO } from "date-fns";
import { useLocale, useTranslations } from "next-intl";
import { Img } from "@/components/image";
import type { RescheduledDiet } from "@/features/cart/hooks/use-reschedule-unavailable-dates";
import { getDateLocale } from "@/lib/helpers/diet-dates";
import { formatDietName } from "@/lib/helpers/format-brand-name";
import { cn } from "@/lib/utils";
import type { LocaleType } from "@/types/locale.type";

const TAG_CLASS_NAME =
  "flex h-7.5 items-center rounded-sm border border-brand-grey2 bg-brand-white px-2 font-medium text-xs leading-4.5";

export const CartRescheduleRow = ({ item }: { item: RescheduledDiet }) => {
  const t = useTranslations();
  const locale = useLocale() as LocaleType;
  const { catering } = item.diet;

  const formatRange = (dates: string[]) => {
    const days = dates.map((date) => parseISO(date));
    const dateLocale = getDateLocale(locale);

    return t("common.daysRangeSimple", {
      from: format(min(days), "dd MMM", { locale: dateLocale }),
      to: format(max(days), "dd MMM", { locale: dateLocale }),
    });
  };

  return (
    <li className="flex items-start gap-2 py-3 first:pt-0 last:pb-0 md:gap-3">
      <span className="flex size-6 shrink-0 items-center justify-center overflow-hidden rounded-[4px] bg-brand-white p-0.5 md:size-14 md:rounded-md md:p-1">
        <Img
          alt={catering.logo.alt ?? catering.name}
          className="size-full object-contain"
          height={48}
          src={catering.logo.url}
          width={48}
        />
      </span>
      <div className="flex min-w-0 flex-1 flex-col gap-1.5">
        <p className="text-base">
          <span className="font-medium text-brand-green1-300">
            {formatDietName(item.diet.name)}
          </span>
          <span
            aria-hidden
            className="mx-1.5 inline-block size-1 rounded-full bg-brand-green1-100 align-middle"
          />
          <span className="text-text-subtle">{catering.name}</span>
        </p>
        <div className="flex flex-wrap items-center gap-1.5">
          <span
            className={cn(TAG_CLASS_NAME, "text-brand-grey2-400 line-through")}
          >
            {formatRange(item.previousDates)}
          </span>
          <IconArrowRight
            className="size-6 shrink-0 text-brand-green1-300"
            stroke={1.5}
          />
          <span className={cn(TAG_CLASS_NAME, "text-text-subtle")}>
            {formatRange(item.dates)}
          </span>
        </div>
        <p className="text-brand-green1-300 text-sm">
          {t("cart.rescheduleDialog.changedDays", { count: item.movedDays })}
        </p>
      </div>
    </li>
  );
};
